<script setup lang="ts">
// Settings › Your storage › Attach a bucket (docs/STORAGE.md §11.2): a bucket the server can't
// reach. Its details and keys stay on this device and, sealed, in records only my devices (and
// the people I share libraries with) can open. Two keys: a read-only one for everyone who can
// read my libraries, and a read-write one for uploading; or read-only storage, with no write
// key here at all. Checked from this device, through the relay, before it's attached.
import { computed, reactive, ref } from 'vue'
import { describeError } from '../api/http'
import type { StorageLocation } from '../api/types'
import { checkBucket, type BucketRecord } from '../lib/sealedStorage'
import { useSession } from '../stores/session'

const emit = defineEmits<{ added: [location: StorageLocation]; cancel: [] }>()
const session = useSession()
const busy = ref(false)
const form = reactive({
  name: '',
  endpoint: '',
  region: '',
  bucket: '',
  prefix: '',
  readId: '',
  readSecret: '',
  writeId: '',
  writeSecret: '',
  readOnly: false,
})
const ready = computed(
  () =>
    form.name.trim() &&
    form.bucket.trim() &&
    form.readId.trim() &&
    form.readSecret &&
    (form.readOnly || (form.writeId.trim() && form.writeSecret)),
)

async function add(): Promise<void> {
  const engine = session.engine
  if (!engine) return
  const rec: BucketRecord = {
    name: form.name.trim(),
    ...(form.endpoint.trim() ? { endpoint: form.endpoint.trim().replace(/\/+$/, '') } : {}),
    region: form.region.trim() || 'us-east-1',
    bucket: form.bucket.trim(),
    prefix: form.prefix.trim().replace(/^\/+|\/+$/g, ''),
    read: { id: form.readId.trim(), secret: form.readSecret },
    ...(form.readOnly ? {} : { write: { id: form.writeId.trim(), secret: form.writeSecret } }),
  }
  busy.value = true
  try {
    await checkBucket(session.api, rec, rec.read, rec.write)
    emit('added', await engine.attachBucket(rec))
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <form class="mt-4 grid gap-3 border border-border p-4 md:grid-cols-2" data-testid="bucket-add" @submit.prevent="add">
    <p class="mono text-text-muted md:col-span-2">
      The server never gets this bucket's address or keys: they stay on your devices, and your devices sign every
      request. Files are encrypted before they go in.
    </p>
    <div>
      <label class="label text-text-muted" for="bucket-name">Name</label>
      <input id="bucket-name" v-model="form.name" class="input" maxlength="48" data-testid="storage-name" />
    </div>
    <div>
      <label class="label text-text-muted" for="bucket-endpoint">Endpoint</label>
      <input id="bucket-endpoint" v-model="form.endpoint" class="input" placeholder="https://s3.example.com (empty for AWS)" data-testid="storage-endpoint" />
    </div>
    <div>
      <label class="label text-text-muted" for="bucket-bucket">Bucket</label>
      <input id="bucket-bucket" v-model="form.bucket" class="input" data-testid="storage-bucket" />
    </div>
    <div>
      <label class="label text-text-muted" for="bucket-prefix">Folder in the bucket (optional)</label>
      <input id="bucket-prefix" v-model="form.prefix" class="input" data-testid="storage-prefix" />
    </div>
    <div>
      <label class="label text-text-muted" for="bucket-region">Region (optional)</label>
      <input id="bucket-region" v-model="form.region" class="input" placeholder="us-east-1" data-testid="storage-region" />
    </div>
    <div class="md:col-span-2">
      <div class="label text-text-muted">Read-only key</div>
      <div class="grid gap-2 md:grid-cols-2">
        <input v-model="form.readId" class="input" autocomplete="off" placeholder="Key ID" aria-label="Read-only key ID" data-testid="storage-read-id" />
        <input v-model="form.readSecret" type="password" class="input" autocomplete="off" placeholder="Secret" aria-label="Read-only key secret" data-testid="storage-read-secret" />
      </div>
      <p class="mono mt-1 text-text-muted">Everyone you share a library with gets this one, so it must only be able to read.</p>
    </div>
    <label class="flex cursor-pointer items-start gap-3 text-sm md:col-span-2">
      <input v-model="form.readOnly" type="checkbox" class="mt-0.5" data-testid="storage-read-only" />
      <span>
        Read-only storage
        <span class="mono block text-text-muted">
          No write key here: nobody can add, change or delete files from hrth, you included. Add files with
          <code>hrth import</code> on a computer that has the write key.
        </span>
      </span>
    </label>
    <div v-if="!form.readOnly" class="md:col-span-2">
      <div class="label text-text-muted">Read-write key</div>
      <div class="grid gap-2 md:grid-cols-2">
        <input v-model="form.writeId" class="input" autocomplete="off" placeholder="Key ID" aria-label="Read-write key ID" data-testid="storage-write-id" />
        <input v-model="form.writeSecret" type="password" class="input" autocomplete="off" placeholder="Secret" aria-label="Read-write key secret" data-testid="storage-write-secret" />
      </div>
      <p class="mono mt-1 text-text-muted">Only you and the people you let upload get this one. Make both keys for this bucket only.</p>
    </div>
    <div class="flex justify-end gap-2 md:col-span-2">
      <button type="button" class="btn-ghost" @click="emit('cancel')">Cancel</button>
      <button class="btn-primary" :disabled="busy || !ready" data-testid="storage-add-save">{{ busy ? 'Checking…' : 'Check and attach' }}</button>
    </div>
  </form>
</template>
